import React from 'react'
import * as rn from 'react-native'

import * as c from '../../common'
import * as r from '../../react-utils'

import Feather from 'react-native-vector-icons/Feather'
import Icon from 'react-native-vector-icons/MaterialCommunityIcons'

import * as gs from '../gStyles'

import Labeled from './Labeled'
import LinearGradient from './LinearGradient'
import View from './View'

export interface ISelect {
  readonly close: () => void
  readonly open: () => void
}

export type SelectProps = c.r<{
  childRef?: React.RefObject<rn.TextInput | null | undefined>
  children?: React.ReactElement
  disabled?: boolean
  grow?: boolean
  guideline?: string
  guidelineType?: c.Guideline['type']
  hide?: boolean
  inline?: boolean
  label?: false | string
  labelShowAsterisk?: boolean
  multiple?: boolean
  noModal?: boolean
  on: c.Surface
  /**
   * Returns the new set of values (space,slash,space-separated) resulting from interaction
   * if `multiple` is set to `true`.
   */
  onChange?: (value: string) => void
  onRequestClose?: c.VoidFn
  open?: boolean
  openToTheLeft?: boolean
  opts: c.Opts
  required?: boolean
  /**
   * space,slash,space-separated list of values if `multiple` is set to `true`.
   */
  value?: string
  warn?: boolean
}>

const initDropdownGeometry = Object.freeze({
  left: -1,
  maxHeight: -1,
  top: -1,
  width: -1,
})

const SelectForwarded = React.forwardRef<ISelect | undefined, SelectProps>(
  function Select(
    {
      childRef,
      children,
      disabled,
      grow,
      guideline: guidelineProp,
      guidelineType: guidelineTypeProp,
      hide,
      inline,
      label,
      labelShowAsterisk,
      multiple,
      noModal,
      on,
      onChange,
      onRequestClose,
      open: openProp,
      openToTheLeft,
      opts,
      required,
      value: selectedValue,
      warn,
    },
    ref,
  ): React.ReactElement | null {
    const t = r.useTheme()
    const styles = gs.useThemedStyleSheet(themedStyles, on)
    const gStyles = gs.useGlobalStyles(on)
    const SelectSeparator = React.useMemo(
      () => React.memo(() => <rn.View style={gStyles.separatorH} />),
      [gStyles.separatorH],
    )

    const [isVisible, setIsVisible, toggleIsVisible] = r.useBool(false)
    const [canScrollUp, setCanScrollUp] = React.useState(false)
    const [canScrollDown, setCanScrollDown] = React.useState(false)
    const listMeasureRef = React.useRef({
      contentHeight: 0,
      height: 0,
      offsetY: 0,
    })

    const [dropdownGeometry, setDropdownGeometry] = React.useState<{
      bottom?: number
      maxHeight?: number
      left: number
      top?: number
      width: number
    }>(initDropdownGeometry)

    //#region refs
    const buttonRef = React.useRef<rn.View | null>(null)
    const flatListRef = React.useRef<rn.FlatList | null>(null)
    const instance = React.useMemo(
      () => ({
        close() {
          setIsVisible(false)
        },
        open() {
          setIsVisible(true)
        },
      }),
      [setIsVisible],
    )
    React.useImperativeHandle(ref, () => instance)
    //#endregion refs

    //#region local
    const selectedNotInOpts = React.useMemo(
      () =>
        !multiple &&
        !!selectedValue &&
        !opts.find((o) => o.value === selectedValue),
      [multiple, opts, selectedValue],
    )
    const guideline = (() => {
      if (guidelineProp) return !(isVisible || openProp) && guidelineProp
      if (selectedNotInOpts) return 'Opt no longer avail., reset adder'
      if (required && !selectedValue) return 'Required'
      return undefined
    })()
    const guidelineType = (() => {
      if (guidelineTypeProp) return guidelineTypeProp
      if (selectedNotInOpts) return 'warning'
      if (required && !selectedValue) return 'warning'
      return undefined
    })()
    const initialScrollIndex = Math.max(
      0,
      opts.findIndex((x) => x.value === selectedValue),
    )
    //#endregion local
    const calcGeometry = React.useCallback((): void => {
      const node = (childRef || buttonRef).current
      if (!node) return

      const apply = (
        _width: number,
        height: number,
        pageX: number,
        pageY: number,
      ) => {
        const screenHeight = r.isWeb
          ? window.innerHeight
          : rn.Dimensions.get('window').height
        const spaceBelow = screenHeight - pageY - height
        const maxHeight = screenHeight * 0.4
        const width = children ? c.fieldWidthWeb : _width
        const left = openToTheLeft ? pageX - width : pageX

        if (spaceBelow >= maxHeight) {
          setDropdownGeometry({ left, maxHeight, top: pageY + height, width })
        } else {
          setDropdownGeometry({
            bottom: screenHeight - pageY,
            left,
            maxHeight,
            width,
          })
        }
      }

      if (r.isWeb) {
        const rect = (node as unknown as HTMLElement).getBoundingClientRect()
        apply(rect.width, rect.height, rect.left, rect.top)
      } else {
        node.measure((_, __, w, h, px, py) => {
          apply(w, h, px, py)
        })
      }
    }, [childRef, children, openToTheLeft])

    React.useEffect(() => {
      if (isVisible || (openProp && !inline)) {
        calcGeometry()

        if (r.isWeb) {
          // @ts-ignore
          document.body.style.overflow = 'hidden'
        }
      }

      return () => {
        setDropdownGeometry(initDropdownGeometry)
        if (r.isWeb) {
          // @ts-ignore
          document.body.style.overflow = 'unset'
        }
      }
    }, [calcGeometry, children, inline, isVisible, openProp])

    React.useEffect(() => {
      if (isVisible || openProp) {
        if (initialScrollIndex > 0) setCanScrollUp(true)
      } else {
        setCanScrollUp(false)
        setCanScrollDown(false)
        listMeasureRef.current = { contentHeight: 0, height: 0, offsetY: 0 }
      }
    }, [initialScrollIndex, isVisible, openProp])

    const updateScrollIndicators = React.useCallback(() => {
      const { contentHeight, height, offsetY } = listMeasureRef.current
      setCanScrollUp(offsetY > 2)
      setCanScrollDown(height > 0 && offsetY + height < contentHeight - 2)
    }, [])

    const handleFlatListLayout = React.useCallback(
      (e: rn.LayoutChangeEvent) => {
        listMeasureRef.current.height = e.nativeEvent.layout.height
        updateScrollIndicators()
      },
      [updateScrollIndicators],
    )

    const handleContentSizeChange = React.useCallback(
      (_: number, h: number) => {
        listMeasureRef.current.contentHeight = h
        updateScrollIndicators()
      },
      [updateScrollIndicators],
    )

    const handleScroll = React.useCallback(
      (e: rn.NativeSyntheticEvent<rn.NativeScrollEvent>) => {
        const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent
        listMeasureRef.current = {
          contentHeight: contentSize.height,
          height: layoutMeasurement.height,
          offsetY: contentOffset.y,
        }
        updateScrollIndicators()
      },
      [updateScrollIndicators],
    )

    const handleScrollToTop = React.useCallback(() => {
      flatListRef.current?.scrollToOffset({ animated: true, offset: 0 })
    }, [])

    const handleScrollToBottom = React.useCallback(() => {
      flatListRef.current?.scrollToEnd({ animated: true })
    }, [])

    const handleSelect = React.useCallback(
      (value: string) => {
        if (multiple) {
          const selectedValues =
            selectedValue === '' ? [] : selectedValue?.split(' / ') || []
          const alreadySelected = selectedValues.includes(value)
          if (alreadySelected) {
            onChange?.(selectedValues.filter((v) => v !== value).join(' / '))
          } else {
            onChange?.(selectedValues.concat(value).sort().join(' / '))
          }
        } else {
          onChange?.(value)
          setIsVisible(false)
        }
      },
      [multiple, onChange, selectedValue, setIsVisible],
    )

    const renderItem = React.useCallback(
      ({ item }: { item: c.Opt }): React.ReactElement => (
        <DropdownItem
          // Bug here
          isSelected={
            multiple
              ? Boolean(selectedValue?.split(' / ').includes(item.value))
              : selectedValue === item.value
          }
          label={item.label}
          on={on}
          onSelect={handleSelect}
          value={item.value}
        />
      ),
      [handleSelect, multiple, on, selectedValue],
    )

    const dropdownWrapperStyle = React.useMemo(
      () => [
        gs.absolute,
        styles.dropdownWrapper,
        {
          bottom: dropdownGeometry.bottom,
          left: dropdownGeometry.left,
          top: dropdownGeometry.top,
          width: dropdownGeometry.width,
        },
        noModal && gs.zIndex1, // TODO: revisit.
      ],
      [
        dropdownGeometry.bottom,
        dropdownGeometry.left,
        dropdownGeometry.top,
        dropdownGeometry.width,
        noModal,
        styles.dropdownWrapper,
      ],
    )

    const flatListStyle = React.useMemo(
      () => [styles.dropdownInner, { maxHeight: dropdownGeometry.maxHeight }],
      [dropdownGeometry.maxHeight, styles.dropdownInner],
    )

    const closeModal = React.useCallback((): void => {
      setIsVisible(false)
    }, [setIsVisible])

    const iconColor = t.input[on].color as string
    const gradientBgOpaque = t.input[on].backgroundColor as string
    const gradientBgTransparent = gradientBgOpaque?.startsWith('#')
      ? `${gradientBgOpaque}00`
      : 'transparent'

    const renderDropdown = React.useCallback((): React.ReactElement | null => {
      const flatListNode = (
        <rn.View style={dropdownWrapperStyle}>
          <rn.FlatList
            ItemSeparatorComponent={SelectSeparator}
            data={opts}
            initialScrollIndex={initialScrollIndex}
            keyExtractor={keyExtractor}
            onContentSizeChange={handleContentSizeChange}
            onLayout={handleFlatListLayout}
            onScroll={handleScroll}
            onScrollToIndexFailed={console.log}
            ref={flatListRef}
            renderItem={renderItem}
            scrollEventThrottle={16}
            style={flatListStyle}
          />
          {canScrollUp && (
            <rn.View pointerEvents="box-none" style={styles.scrollIndicatorTop}>
              <rn.Pressable onPress={handleScrollToTop}>
                <LinearGradient
                  colors={[gradientBgOpaque, gradientBgTransparent]}
                  style={styles.scrollGradientTop}
                >
                  <Icon
                    color={iconColor}
                    name="chevron-up"
                    size={14}
                    style={gs.userSelectNone}
                  />
                </LinearGradient>
              </rn.Pressable>
            </rn.View>
          )}
          {canScrollDown && (
            <rn.View
              pointerEvents="box-none"
              style={styles.scrollIndicatorBottom}
            >
              <rn.Pressable onPress={handleScrollToBottom}>
                <LinearGradient
                  colors={[gradientBgTransparent, gradientBgOpaque]}
                  style={styles.scrollGradientBottom}
                >
                  <Icon
                    color={iconColor}
                    name="chevron-down"
                    size={14}
                    style={gs.userSelectNone}
                  />
                </LinearGradient>
              </rn.Pressable>
            </rn.View>
          )}
        </rn.View>
      )

      if (noModal) return openProp || isVisible ? flatListNode : <></>

      if (c.values(dropdownGeometry).every((x) => x === -1)) return null

      return (
        <rn.Modal
          animationType="none"
          onRequestClose={onRequestClose || toggleIsVisible}
          transparent
          visible={openProp || isVisible}
        >
          <rn.TouchableOpacity
            onPress={onRequestClose || closeModal}
            style={styles.backdrop}
          >
            {flatListNode}
          </rn.TouchableOpacity>
        </rn.Modal>
      )
    }, [
      SelectSeparator,
      canScrollDown,
      canScrollUp,
      dropdownGeometry,
      dropdownWrapperStyle,
      flatListStyle,
      handleContentSizeChange,
      handleFlatListLayout,
      gradientBgOpaque,
      gradientBgTransparent,
      handleScroll,
      handleScrollToBottom,
      handleScrollToTop,
      iconColor,
      initialScrollIndex,
      isVisible,
      noModal,
      onRequestClose,
      openProp,
      opts,
      renderItem,
      styles.backdrop,
      styles.scrollGradientBottom,
      styles.scrollGradientTop,
      styles.scrollIndicatorBottom,
      styles.scrollIndicatorTop,
      toggleIsVisible,
      closeModal,
    ])

    const buttonTextStyle = React.useMemo(
      () => [styles.btnTxt, !selectedValue && styles.placeholder],
      [selectedValue, styles.btnTxt, styles.placeholder],
    )

    if (disabled || hide || selectedNotInOpts || opts.length === 0) {
      const disabledBtn = (
        <View
          style={
            !grow && !label ? styles.btnDisabledShrink : styles.btnDisabled
          }
        >
          <rn.Text style={styles.btnDisabledTxt}>
            {multiple && selectedValue
              ? selectedValue
              : opts.find((o) => o.value === selectedValue)?.label ||
                selectedValue ||
                'Select...'}
          </rn.Text>
        </View>
      )

      return label ? (
        <Labeled
          disabled
          grow={grow}
          guideline={guideline}
          guidelineType={guidelineType}
          hide={hide}
          label={label}
          on={on}
          showAsterisk={labelShowAsterisk}
        >
          {children || disabledBtn}
        </Labeled>
      ) : (
        children || disabledBtn
      )
    }

    if (inline && !openProp) return null
    if (inline) {
      const el = (
        <rn.View style={styles.dropdownInline}>
          {opts.map((o) => (
            <React.Fragment key={o.value}>
              <DropdownItem
                isSelected={
                  multiple
                    ? Boolean(selectedValue?.split(' / ').includes(o.value))
                    : selectedValue === o.value
                }
                label={o.label}
                multiple={multiple}
                on={on}
                onSelect={handleSelect}
                value={o.value}
              />
              <SelectSeparator />
            </React.Fragment>
          ))}
        </rn.View>
      )

      if (label) {
        return (
          <Labeled
            grow={grow}
            guideline={guideline}
            guidelineType={guidelineType}
            label={label}
            on={on}
            showAsterisk={labelShowAsterisk}
          >
            {el}
          </Labeled>
        )
      }

      return el
    }

    const btnStyle = (() => {
      if (!grow && !label) {
        if (warn) {
          return styles.btnShrinkWarn
        }
        return isVisible || openProp
          ? styles.btnShrinkFocused
          : styles.btnShrink
      }
      if (warn) {
        return styles.btnWarn
      }
      return isVisible || openProp ? styles.btnFocused : styles.btn
    })()
    const btn = (
      <rn.Pressable onPress={toggleIsVisible} ref={buttonRef} style={btnStyle}>
        <rn.Text style={buttonTextStyle}>
          {multiple && selectedValue
            ? selectedValue
            : opts.find((o) => o.value === selectedValue)?.label || 'Select...'}
        </rn.Text>

        <Icon
          color={t.input[on].color as string}
          name={isVisible ? 'chevron-up' : 'chevron-down'}
          size={16}
          style={gs.userSelectNone}
        />
      </rn.Pressable>
    )

    return (
      <>
        {label ? (
          <Labeled
            grow={grow}
            guideline={guideline}
            guidelineType={guidelineType}
            label={label}
            on={on}
            showAsterisk={labelShowAsterisk}
          >
            {children || btn}
          </Labeled>
        ) : (
          children || btn
        )}
        {renderDropdown()}
      </>
    )
  },
)

const keyExtractor = (item: c.Opt): string => item.value

interface ItemProps extends c.Opt {
  readonly isSelected: boolean
  readonly multiple?: boolean
  readonly on: c.Surface
  readonly onSelect: (value: string) => void
}

const DropdownItem = React.memo<ItemProps>(function DropdownItem({
  isSelected,
  label,
  multiple,
  on,
  onSelect,
  value,
}) {
  const t = r.useTheme()
  const styles = gs.useThemedStyleSheet(themedStyles, on)

  const handlePress = React.useCallback(() => {
    onSelect(value)
  }, [onSelect, value])

  return (
    <rn.Pressable
      onPress={handlePress}
      style={multiple ? styles.itemMultiple : styles.item}
    >
      {multiple && (
        <>
          {isSelected ? (
            <Feather
              color={t.input[on].highlight}
              name="check-square"
              size={16}
              style={gs.userSelectNone}
            />
          ) : (
            <Feather
              color={t.input[on].opaque}
              name="square"
              size={16}
              style={gs.userSelectNone}
            />
          )}

          {gs.colGap24}
        </>
      )}

      <rn.Text style={styles.itemText}>{label}</rn.Text>

      {!multiple && isSelected && (
        <Icon
          color={t.input[on].highlight}
          name="check"
          size={16}
          style={gs.userSelectNone}
        />
      )}
    </rn.Pressable>
  )
})

const themedStyles = gs.ThemedStyleSheet.create((t, on) => {
  const btn = {
    ...t.input[on],
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: t.input[on].paddingVertical, // TODO: Doesn't match input height otherwise
  } as const
  const btnShrink = { ...btn, ...gs.inputShrink }
  const btnTxt = {
    fontFamily: t.input[on].fontFamily,
    color: t.input[on].color,
    fontSize: t.input[on].fontSize,
    ...gs.userSelectNone,
  } as const
  const dropdownWrapper = {
    borderRadius: t.input[on].borderRadius,
    ...gs.shadowHalf,
  } as const
  const dropdownInner = {
    backgroundColor: t.input[on].backgroundColor,
    borderRadius: t.input[on].borderRadius,
  } as const
  const item = {
    alignItems: 'center',
    borderRadius: t.borderRadius,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
  } as const

  return {
    backdrop: { ...gs.flexGrow, touchAction: 'none' },
    btn,
    btnDisabled: { ...btn, ...gs.deadCenter, opacity: 0.5 },
    btnDisabledShrink: { ...btn, ...gs.inputShrink, opacity: 0.5 },
    btnDisabledTxt: {
      ...btnTxt,
      opacity: 0.5,
      textAlign: 'center',
      textAlignVertical: 'center',
      ...gs.userSelectNone,
    },
    btnFocused: { ...btn, borderColor: t[on].highlight },
    btnShrink,
    btnShrinkWarn: { ...btnShrink, borderColor: t.warning },
    btnShrinkFocused: {
      ...btn,
      ...gs.inputShrink,
      borderColor: t[on].highlight,
    },
    btnTxt,
    btnTxtPlaceholder: { ...btnTxt, fontFamily: t.input[on].placeholderColor },
    btnWarn: { ...btn, borderColor: t.warning },
    dropdownInline: {
      backgroundColor: t.input[on].backgroundColor,
      borderRadius: t.input[on].borderRadius,
    },
    dropdownInner,
    dropdownWrapper,
    icon: { marginRight: 10 },
    placeholder: { color: t.input[on].placeholderColor },
    item,
    itemMultiple: { ...item, justifyContent: 'flex-start' },
    itemText: {
      color: t.input[on].color,
      fontFamily: t.input[on].fontFamily,
      ...gs.userSelectNone,
    },
    scrollGradientBottom: {
      alignItems: 'center' as const,
      borderBottomLeftRadius: t.input[on].borderRadius,
      borderBottomRightRadius: t.input[on].borderRadius,
      height: 56,
      justifyContent: 'flex-end' as const,
      paddingBottom: 8,
    },
    scrollGradientTop: {
      alignItems: 'center' as const,
      borderTopLeftRadius: t.input[on].borderRadius,
      borderTopRightRadius: t.input[on].borderRadius,
      height: 56,
      justifyContent: 'flex-start' as const,
      paddingTop: 8,
    },
    scrollIndicatorBottom: {
      bottom: 0,
      left: 0,
      position: 'absolute' as const,
      right: 0,
    },
    scrollIndicatorTop: {
      left: 0,
      position: 'absolute' as const,
      right: 0,
      top: 0,
    },
  }
})

export default React.memo(SelectForwarded)

// function isSafariMobile() {
//   if (r.isMobile) return false;
//   return (
//     [
//       'iPad Simulator',
//       'iPhone Simulator',
//       'iPod Simulator',
//       'iPad',
//       'iPhone',
//       'iPod',
//       // @ts-ignore
//     ].includes(navigator.platform) ||
//     // iPad on iOS 13 detection
//     // @ts-ignore
//     (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
//   );
// }
